// Apple-style Scroll Animations for Home Page
// Smooth, elegant animations as sections come into view

class HomeScrollAnimations {
  constructor() {
    this.isMobile = this.checkMobile();
    this.init();
  }

  checkMobile() {
    // Check if device is mobile
    return window.innerWidth <= 749 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  init() {
    // Wait for GSAP and ScrollTrigger
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      setTimeout(() => this.init(), 50);
      return;
    }

    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);

    // Hide bundles section elements IMMEDIATELY before any animations
    this.hideBundlesElements();

    // Initialize animations (now works on mobile and desktop)
    this.animateSections();
    this.animateBundlesSection();
    this.animateHeadings();
    this.animateImages();
    this.animateButtons();
    this.animateProductCards();
    this.animateHeroParallax();

    // Setup resize handler to fix disappearing elements
    this.setupResizeHandler();
  }

  enableMobileMode() {
    // On mobile, ensure all elements are visible and reset any hidden states
    const sections = document.querySelectorAll('#MainContent > section, #MainContent > div.section');
    sections.forEach((section) => {
      section.style.opacity = '1';
      section.style.transform = 'none';
      section.style.visibility = 'visible';
    });

    // Reset hero content
    const heroContent = document.querySelector('.hero-custom__content');
    if (heroContent) {
      heroContent.style.opacity = '1';
      heroContent.style.transform = 'none';
      heroContent.style.visibility = 'visible';
    }

    // Reset hero text elements
    const heroHeading = document.querySelector('.hero-custom__heading');
    const heroSubheading = document.querySelector('.hero-custom__subheading');
    const heroText = document.querySelector('.hero-custom__text');
    const heroButtons = document.querySelectorAll('.hero-custom__buttons .button');
    
    [heroHeading, heroSubheading, heroText].forEach((el) => {
      if (el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.style.visibility = 'visible';
      }
    });

    heroButtons.forEach((btn) => {
      btn.style.opacity = '1';
      btn.style.transform = 'none';
      btn.style.visibility = 'visible';
    });

    // Reset bundles section
    const bundlesElements = document.querySelectorAll('.bundles-section__header, .bundles-section__subheading, .bundle-promo-card__badge, .bundle-promo-card__content, .bundle-promo-card__image');
    bundlesElements.forEach((el) => {
      el.style.opacity = '1';
      el.style.visibility = 'visible';
    });

    // Reset all images
    const images = document.querySelectorAll('#MainContent img');
    images.forEach((img) => {
      img.style.opacity = '1';
      img.style.transform = 'none';
    });

    // Reset all buttons
    const buttons = document.querySelectorAll('#MainContent .button, #MainContent a[class*="button"]');
    buttons.forEach((btn) => {
      btn.style.opacity = '1';
      btn.style.transform = 'none';
    });
  }

  hideBundlesElements() {
    // Don't hide on mobile
    if (this.isMobile) return;

    // Immediately hide bundles section text elements to prevent flash
    const bundlesSection = document.querySelector('.bundles-section');
    if (!bundlesSection) return;

    const bundleHeader = bundlesSection.querySelector('.bundles-section__header');
    const bundleSubheading = bundlesSection.querySelector('.bundles-section__subheading');
    const bundleHeading = bundlesSection.querySelector('.bundles-section__heading');
    const bundleBadge = bundlesSection.querySelector('.bundle-promo-card__badge');
    const bundleContent = bundlesSection.querySelector('.bundle-promo-card__content');
    const bundleImage = bundlesSection.querySelector('.bundle-promo-card__image');

    // Use inline styles for immediate effect (before GSAP)
    if (bundleHeader) bundleHeader.style.opacity = '0';
    if (bundleSubheading) bundleSubheading.style.opacity = '0';
    if (bundleHeading) bundleHeading.style.opacity = '0';
    if (bundleBadge) bundleBadge.style.opacity = '0';
    if (bundleContent) bundleContent.style.opacity = '0';
    if (bundleImage) bundleImage.style.opacity = '0';
  }

  animateBundlesSection() {
    // Special animation for bundles section - image first, then content
    const bundlesSection = document.querySelector('.bundles-section');
    if (!bundlesSection) return;

    // Override the section animation - keep it hidden initially
    gsap.set(bundlesSection, {
      opacity: 1, // Section container stays visible
      y: 0,
      scale: 1,
    });

    const bundleImage = bundlesSection.querySelector('.bundle-promo-card__image');
    const bundleImageWrapper = bundlesSection.querySelector('.bundle-promo-card__image-wrapper');
    const bundleContent = bundlesSection.querySelector('.bundle-promo-card__content');
    const bundleBadge = bundlesSection.querySelector('.bundle-promo-card__badge');
    const bundleHeader = bundlesSection.querySelector('.bundles-section__header');
    const bundleSubheading = bundlesSection.querySelector('.bundles-section__subheading');
    const bundleHeading = bundlesSection.querySelector('.bundles-section__heading');

    // Set initial states - hide ALL text elements immediately
    if (bundleHeader) {
      gsap.set(bundleHeader, { opacity: 0, y: 30 });
    }
    if (bundleSubheading) {
      gsap.set(bundleSubheading, { opacity: 0, y: 30 });
    }
    if (bundleHeading) {
      gsap.set(bundleHeading, { opacity: 0, y: 30 });
    }
    if (bundleBadge) {
      gsap.set(bundleBadge, { opacity: 0, y: 30 });
    }
    if (bundleContent) {
      gsap.set(bundleContent, { opacity: 0, y: 30 });
    }
    if (bundleImage) {
      gsap.set(bundleImage, { opacity: 0, scale: 0.95, y: 20 });
    }

    // Create timeline for bundles section
    const bundlesTL = gsap.timeline({
      scrollTrigger: {
        trigger: bundlesSection,
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      },
    });

    // First show the image
    if (bundleImage) {
      bundlesTL.to(bundleImage, 
        { opacity: 1, scale: 1, y: 0, duration: 1, ease: 'power2.out' }
      );
    }

    // Then show header (heading and subheading together) after image
    if (bundleHeader) {
      bundlesTL.to(bundleHeader,
        { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' },
        '-=0.3'
      );
    } else {
      // Fallback if header wrapper doesn't exist
      if (bundleHeading) {
        bundlesTL.to(bundleHeading,
          { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' },
          '-=0.3'
        );
      }
      if (bundleSubheading) {
        bundlesTL.to(bundleSubheading,
          { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' },
          '-=0.7'
        );
      }
    }

    // Then show badge and content together
    if (bundleBadge) {
      bundlesTL.to(bundleBadge,
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' },
        '-=0.4'
      );
    }
    if (bundleContent) {
      bundlesTL.to(bundleContent,
        { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' },
        '-=0.5'
      );
    }
  }

  animateSections() {
    // Animate main sections as they come into view - Apple style
    const sections = document.querySelectorAll('#MainContent > section:not(.hero-custom), #MainContent > div.section');
    
    sections.forEach((section, index) => {
      // Skip bundles section - it has its own animation
      if (section.classList.contains('bundles-section')) return;

      // Set initial state
      gsap.set(section, {
        opacity: 0,
        y: 80,
        scale: 0.98,
      });

      // Create timeline for smoother animation
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
          end: 'top 30%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.to(section, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1.4,
        ease: 'power3.out',
      });
    });
  }

  animateHeadings() {
    // Animate headings with smooth fade and slide
    // But for bundles section, animate with the section, not individually
    const headings = document.querySelectorAll('#MainContent h1, #MainContent h2, #MainContent h3');
    
    headings.forEach((heading) => {
      const parent = heading.closest('section');
      if (!parent || parent.classList.contains('hero-custom')) return;

      // For bundles section, don't animate headings separately - they'll animate with the section
      if (parent.classList.contains('bundles-section')) return;

      gsap.set(heading, {
        opacity: 0,
        y: 50,
      });

      gsap.to(heading, {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: heading,
          start: 'top 75%',
          toggleActions: 'play none none reverse',
        },
      });
    });
  }

  animateImages() {
    // Animate images with smooth scale and fade - Apple style
    const images = document.querySelectorAll('#MainContent img:not(.hero-custom img)');
    
    images.forEach((img) => {
      const parent = img.closest('section');
      if (!parent || parent.classList.contains('hero-custom')) return;

      // For bundles section, animate image first, then content
      if (parent.classList.contains('bundles-section')) {
        // Image should appear first
        gsap.set(img, {
          opacity: 0,
          scale: 0.95,
          y: 20,
        });

        gsap.to(img, {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: parent,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        });
        return;
      }

      gsap.set(img, {
        opacity: 0,
        scale: 0.92,
        y: 30,
      });

      gsap.to(img, {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 1.2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: img,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
      });
    });
  }

  animateButtons() {
    // Animate buttons with slide-up
    const buttons = document.querySelectorAll('#MainContent .button, #MainContent a[class*="button"]');
    
    buttons.forEach((button) => {
      const parent = button.closest('section');
      if (!parent || parent.classList.contains('hero-custom')) return;

      // For bundles section, animate buttons with content, not separately
      if (parent.classList.contains('bundles-section')) {
        gsap.set(button, {
          opacity: 0,
          y: 20,
        });

        // Animate after image appears
        gsap.to(button, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          delay: 0.3,
          scrollTrigger: {
            trigger: parent,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        });
        return;
      }

      gsap.set(button, {
        opacity: 0,
        y: 20,
      });

      gsap.to(button, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: button,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      });
    });
  }

  animateProductCards() {
    // Animate product cards with elegant stagger - Apple style
    // Find all sections that contain product cards or resource list items
    const sections = document.querySelectorAll('#MainContent section, #MainContent div.section');
    
    sections.forEach((section) => {
      const productCards = section.querySelectorAll('.product-card, product-card, .resource-list__item');
      
      if (productCards.length > 0) {
        gsap.set(productCards, {
          opacity: 0,
          y: 60,
          scale: 0.96,
        });

        gsap.to(productCards, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1,
          ease: 'power2.out',
          stagger: {
            amount: 0.4,
            from: 'start',
          },
          scrollTrigger: {
            trigger: section,
            start: 'top 75%',
            toggleActions: 'play none none reverse',
          },
        });
      }
    });
  }

  // Add parallax effect to hero section
  animateHeroParallax() {
    const hero = document.querySelector('.hero-custom');
    if (!hero) return;

    const heroContent = hero.querySelector('.hero-custom__content');
    if (!heroContent) return;

    gsap.to(heroContent, {
      y: -50,
      opacity: 0.7,
      ease: 'none',
      scrollTrigger: {
        trigger: hero,
        start: 'top top',
        end: 'bottom top',
        scrub: 1,
      },
    });
  }

  setupResizeHandler() {
    // Handle window resize to fix disappearing elements
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = this.checkMobile();

        // If switching between mobile/desktop, reinitialize
        if (wasMobile !== this.isMobile) {
          if (this.isMobile) {
            // Kill all ScrollTrigger instances
            if (typeof ScrollTrigger !== 'undefined') {
              ScrollTrigger.getAll().forEach(trigger => trigger.kill());
            }
            this.enableMobileMode();
          } else {
            // Reinitialize desktop animations
            window.location.reload();
          }
          return;
        }

        // If still desktop, refresh ScrollTrigger and ensure visibility
        if (!this.isMobile && typeof ScrollTrigger !== 'undefined') {
          // Store current scroll position
          const scrollY = window.scrollY;
          
          // Refresh all ScrollTrigger instances
          ScrollTrigger.refresh();
          
          // AFTER refresh, ensure visible elements stay visible
          // Use requestAnimationFrame to ensure this happens after ScrollTrigger updates
          requestAnimationFrame(() => {
            this.resetVisibleElements(scrollY);
          });
        } else if (this.isMobile) {
          // Ensure mobile mode is properly applied
          this.enableMobileMode();
        }
      }, 150); // Debounce resize events
    };

    window.addEventListener('resize', handleResize, { passive: true });
  }

  resetVisibleElements(scrollY) {
    // Get all sections that might have been animated
    const sections = document.querySelectorAll('#MainContent > section, #MainContent > div.section');
    
    sections.forEach(section => {
      const rect = section.getBoundingClientRect();
      const isInView = rect.top < window.innerHeight && rect.bottom > 0;
      
      // If section is in view, ensure its content is visible
      if (isInView) {
        // Reset section itself
        gsap.set(section, { opacity: 1, y: 0, scale: 1, clearProps: 'transform,opacity' });
        
        // Reset headings
        const headings = section.querySelectorAll('h1, h2, h3');
        headings.forEach(h => {
          gsap.set(h, { opacity: 1, y: 0, clearProps: 'transform,opacity' });
        });
        
        // Reset images
        const images = section.querySelectorAll('img');
        images.forEach(img => {
          gsap.set(img, { opacity: 1, scale: 1, y: 0, clearProps: 'transform,opacity' });
        });
        
        // Reset buttons
        const buttons = section.querySelectorAll('.button, a[class*="button"]');
        buttons.forEach(btn => {
          gsap.set(btn, { opacity: 1, y: 0, clearProps: 'transform,opacity' });
        });
      }
    });
  }
}

// Initialize on home page
function waitForGSAP(callback, maxAttempts = 50) {
  if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
    callback();
  } else if (maxAttempts > 0) {
    setTimeout(() => waitForGSAP(callback, maxAttempts - 1), 100);
  }
}

function initHomeScrollAnimations() {
  // Only on home page
  const isHomePage = window.location.pathname === '/' || 
                     window.location.pathname === '/index' || 
                     window.location.pathname === '';
  
  if (document.querySelector('.hero-custom') && isHomePage) {
    new HomeScrollAnimations();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    waitForGSAP(initHomeScrollAnimations);
  });
} else {
  waitForGSAP(initHomeScrollAnimations);
}

